/**
 * Portal v2 report detail (`/portal/reports/$reportId`).
 *
 * The list row answers "how did this report score"; this page answers "who
 * sent what, and which mechanism failed" — the per-record breakdown is the
 * whole reason to click through.
 *
 * View-only. Records paginate because a high-volume report carries thousands
 * of rows, and totals always describe the whole report rather than the page so
 * the header cannot contradict the list the customer came from.
 */
import { useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { ArrowLeft, FileText } from "lucide-react";
import { Button, Skeleton } from "@ddmarc/ui";
import { portalApi } from "@/lib/portal-api";
import { CardV2, KpiTile, SectionHeader, StatPill } from "@/components/primitives";

const passRateTone = (pct: number): "good" | "warn" | "danger" =>
  pct >= 95 ? "good" : pct >= 80 ? "warn" : "danger";

/** pass → good, fail → danger, anything else (none/temperror) stays neutral. */
const resultTone = (result: string | null): "good" | "danger" | "neutral" =>
  result === "pass" ? "good" : result === "fail" ? "danger" : "neutral";

export function PortalReportDetailV2Page() {
  const { reportId } = useParams({ from: "/portal/reports/$reportId" });
  const [page, setPage] = useState(1);
  const size = 50;

  const id = Number(reportId);
  const { data, isLoading, isError, isFetching } = useQuery({
    queryKey: ["portal", "report", id, page],
    queryFn: () => portalApi.getReport(id, { page, size }),
  });

  const backLink = (
    <Link to="/portal/reports">
      <Button variant="ghost" size="sm" className="gap-1.5 px-2 text-[12.5px]">
        <ArrowLeft className="h-3.5 w-3.5" />
        Reports
      </Button>
    </Link>
  );

  if (isLoading) {
    return (
      <div className="space-y-3.5">
        {backLink}
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  // A report outside the customer's organisation 404s server-side; say it does
  // not exist rather than hinting that it belongs to somebody else.
  if (isError || !data) {
    return (
      <div className="space-y-3.5">
        {backLink}
        <CardV2 title="Report not found">
          <p className="text-[12.5px] text-muted-foreground">
            This report does not exist, or is no longer available.
          </p>
        </CardV2>
      </div>
    );
  }

  const pageCount = Math.max(1, Math.ceil(data.records_total / size));

  return (
    <div className="space-y-3.5">
      {backLink}

      <SectionHeader
        title={
          <span className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Report #{data.id}
          </span>
        }
        subtitle={`${data.org_name} · ${format(new Date(data.date_range_begin), "MMM d")} – ${format(
          new Date(data.date_range_end),
          "MMM d, yyyy"
        )}`}
      />

      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
        <KpiTile label="Domain" value={data.domain} hint="Reported for" />
        <KpiTile label="Messages" value={data.total_emails.toLocaleString()} hint="In this report" />
        <KpiTile
          label="Pass rate"
          value={
            <StatPill tone={passRateTone(data.pass_rate)}>
              {data.pass_rate.toFixed(1)}%
            </StatPill>
          }
          hint={`${data.passed_emails.toLocaleString()} passed`}
        />
        <KpiTile
          label="Policy"
          value={data.policy_p ?? "—"}
          hint={data.policy_pct !== null ? `pct=${data.policy_pct}` : "Applied by receiver"}
        />
      </div>

      <CardV2 title="Authentication results" bodyClassName="p-0">
        {data.records.length === 0 ? (
          <p className="px-4 py-10 text-center text-[12.5px] text-muted-foreground">
            This report contains no records.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <div className="min-w-[720px] divide-y divide-border">
              <div className="grid grid-cols-[1.3fr_0.7fr_0.7fr_0.7fr_0.8fr_1.2fr] gap-3 px-4 py-2.5 text-[10.5px] font-semibold uppercase tracking-[0.05em] text-[var(--v2-fg-subtle)]">
                <span>Source IP</span>
                <span className="text-right">Messages</span>
                <span>DKIM</span>
                <span>SPF</span>
                <span>Disposition</span>
                <span>Organization</span>
              </div>
              {data.records.map((record) => (
                <div
                  key={record.id}
                  className="grid grid-cols-[1.3fr_0.7fr_0.7fr_0.7fr_0.8fr_1.2fr] items-center gap-3 px-4 py-[var(--v2-row-py,0.625rem)] text-[12.5px]"
                >
                  <Link
                    to="/portal/senders/$sourceIp"
                    params={{ sourceIp: record.source_ip }}
                    className="truncate font-mono-data text-foreground underline-offset-2 hover:underline"
                  >
                    {record.source_ip}
                  </Link>
                  <span className="tnum text-right text-foreground">
                    {record.count.toLocaleString()}
                  </span>
                  <span>
                    <StatPill tone={resultTone(record.dkim_result)}>
                      {record.dkim_result ?? "—"}
                    </StatPill>
                  </span>
                  <span>
                    <StatPill tone={resultTone(record.spf_result)}>
                      {record.spf_result ?? "—"}
                    </StatPill>
                  </span>
                  <span className="truncate text-muted-foreground">
                    {record.disposition ?? "—"}
                  </span>
                  <span className="truncate text-muted-foreground">
                    {record.asn_org ?? "—"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardV2>

      {pageCount > 1 && (
        <div className="flex items-center justify-between text-[12px] text-muted-foreground">
          <span>
            Page {page} of {pageCount} · {data.records_total.toLocaleString()} records
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1 || isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= pageCount || isFetching}
              onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <p className="text-[12px] text-muted-foreground">
        Pass rate counts messages where DKIM <em>and</em> SPF both passed and aligned — the same
        rule used on the reports list.
      </p>
    </div>
  );
}
